import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

// Nateva's surface→App map, swapped in for surface-apps.default.ts via the
// '@shell-surface-apps' alias when VITE_INSTANCE_SLUG=nateva (vite.config.ts).
// The hard cull: no Omni suite app is imported, so none ships in Nateva's
// bundle. Which of these a build shows comes from the instance's frontDoor
// block (doc customer-front-door, sections 4 and 8):
//   front-door → Apps: the front page, every app this person may open
//                (src/front-door), first tab of the front-door site
//   home       → Programme: where everyone catches up on their projects (My
//                projects first, lead, stage, updates, reports opened in
//                place, the plan and what is waiting on Nateva) (src/programme)
//   learn      → Using Claude: the screens and Claude, and what to ask. On
//                every build, so the Ask Claude button shows on every page.
//   team       → Team: what is going on across Field, Partners, Agreements,
//                Yield and Carbon, from the shared database. Shows only
//                (src/apps/nateva/team)
//   reports    → Reports: a gallery of finished, branded reports by reader, each
//                opened in place with earlier copies and a PDF download, built in
//                nateva-platform (src/apps/nateva/reports). Shows only
//   health     → Health: is everything working and safe, three lights (Data, Software and security,
//                Access) for admins only, from nateva-platform migrations 0095 to 0098. Shows only
//                (src/apps/nateva/health)
//   notifications → Notifications: the page behind the bell (your notices, filters by app and kind,
//                your settings, and for admins every notice and held email copy), from nateva-platform
//                migrations 0085 to 0089 (src/apps/nateva/notifications). Kept off the tabs (navHidden);
//                the bell and the person menu open it
//   brand      → Brand: the Nateva kit's logo, colours, type, voice and document
//                templates with previews and downloads, from the files the deploy
//                exports into src/apps/nateva/brand/kit. Shows only
// Each Nateva app (Field, Partners, Agreements, Yield) is its own repo and
// site; the launcher's tiles open them. Carbon is automations only.
export const surfaceApps: Partial<
  Record<string, LazyExoticComponent<ComponentType>>
> = {
  'front-door': lazy(() => import('./front-door/FrontPage')),
  home: lazy(() => import('./programme/NatevaProgramme')),
  learn: lazy(() => import('./claude/ClaudeGuide')),
  team: lazy(() => import('./apps/nateva/team/TeamSurface')),
  people: lazy(() => import('./apps/nateva/team/PeoplePage')),
  brand: lazy(() => import('./apps/nateva/brand/BrandPage')),
  reports: lazy(() => import('./apps/nateva/reports/NatevaReports')),
  health: lazy(() => import('./apps/nateva/health/HealthPage')),
  notifications: lazy(() => import('./apps/nateva/notifications/NotificationsPage')),
}

// The Programme strip under the apps on the launcher front page (src/front-door/
// launcher/LauncherHome.tsx reads this export): each project's stage and next
// step, each row opening that project on the Programme page.
// The "See it as" bar under the top bar (front-door/launcher/LauncherChrome.tsx).
export { NatevaPeopleBar as PeopleBar } from './apps/nateva/team/NatevaPeopleBar'

export const launcherStrip: LazyExoticComponent<ComponentType> = lazy(() => import('./programme/LauncherStrip'))

// The Programme facts the launcher front page searches and shows (each
// project's stage, next step and what it waits on, and the latest news), read
// from Nateva's database. When this is exported the front page draws its own
// programme card and the strip above is not used.
export { useLauncherFeed } from './programme/launcher-feed'

// My day on the front page, above the app tiles: what is on for the signed-in person today, this week and
// this month across Field, Partners, Agreements, Yield, Carbon and the Programme, with an Everyone view for
// leadership (src/apps/nateva/day; database core.my_day and core.everyone_day, nateva-platform 0100).
export const launcherDay: LazyExoticComponent<ComponentType<{ compact?: boolean }>> = lazy(() => import('./apps/nateva/day/MyDayPanel'))

// One search across every app (database core.search_all, nateva-platform 0101): the front page search box
// (launcher/HomeSearch.tsx) and the Ctrl+K window (components/CommandPalette.tsx via lib/search.ts) both use it.
export { useFoundHits as useSearchAll } from './apps/nateva/day/found'
export { natevaSearchProvider as searchProvider } from './apps/nateva/day/searchProvider'

// Per-person tab visibility: the Health tab is for admins only, who are not seeing it as someone else
// (the database refuses everyone else as well). Every other tab shows as before, so People and the rest
// are unchanged. undefined = not known yet, so the tab stays out of the menu until it is.
export { useNatevaVisibility as useSurfaceVisibility } from './apps/nateva/health/visibility'
// The bell on the launcher's top bar (front-door/launcher/TopBar.tsx renders it): the unread count,
// the latest notices and a link to the Notifications page.
export { NotificationBell } from './apps/nateva/notifications/Bell'
